import MemberPicker from "./MemberPicker";

// Field keys that should render as a MemberPicker (roster dropdown + free text)
// when the caller passes `members`. We keep this list narrow — only fields
// that semantically refer to a shareholder/member of THIS company.
const MEMBER_PICKER_KEYS = new Set([
  "transferor_name",
  "transferee_name",
  "allottee_name",
  "deceased_name",
  "transmittee_name",
]);

// Fields where the picked entity MUST be an existing member of the company
// (cannot be "Other / External"). For a share transfer the transferor has
// to already hold the shares being transferred — they cannot be external.
// The transferee, in contrast, can be a brand-new external shareholder.
const STRICT_MEMBER_KEYS = new Set(["transferor_name"]);

// Companion folio fields that should be auto-filled when a roster member is
// picked. Map from "_name" → "_folio" + which header field to read.
const FOLIO_PREFILL_MAP = {
  transferor_name: "transferor_folio",
  transferee_name: "transferee_folio",
  allottee_name: "allottee_folio",
  deceased_name: "deceased_folio",
};

/**
 * Renders structured input fields for resolution templates that need
 * extra particulars (e.g. transferor/transferee/no_of_shares for share
 * transfer). Field descriptors come from the `specific_fields` array
 * attached to each resolution-type by the backend (`/api/resolution-types`).
 *
 * When `members` is non-empty, name-typed fields (transferor_name,
 * transferee_name, allottee_name) render as MemberPicker dropdowns and
 * picking a roster member auto-fills the companion folio field.
 */
export default function SpecificFieldsPanel({
  fields,
  values,
  onChange,
  title,
  testIdPrefix,
  fieldTestIdPrefix, // optional — defaults to testIdPrefix; lets callers preserve
                     // historical field-testid scheme (e.g. `specific-${idx}` for
                     // field inputs while container stays `specific-fields-${idx}`).
  members = [],
}) {
  if (!fields || fields.length === 0) return null;
  const fieldPrefix = fieldTestIdPrefix || testIdPrefix;
  const setVal = (key, v) => onChange({ ...(values || {}), [key]: v });
  const setMany = (patch) => onChange({ ...(values || {}), ...patch });
  const common =
    "w-full border border-zinc-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white";

  return (
    <div
      className="border border-blue-200 bg-blue-50/40 p-3 mb-2"
      data-testid={testIdPrefix}
    >
      {title && (
        <div className="text-[10px] uppercase tracking-[0.12em] text-[#002FA7] font-semibold mb-2">
          Transaction Particulars · {title}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {fields.map((f) => {
          const val = values?.[f.key] ?? "";
          const isMemberPickerField =
            members.length > 0 && MEMBER_PICKER_KEYS.has(f.key);
          return (
            <label
              key={f.key}
              className={f.type === "textarea" ? "block md:col-span-2" : "block"}
            >
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                {f.label}
                {f.required && <span className="text-red-600"> *</span>}
              </span>
              {isMemberPickerField ? (
                <div className="mt-0.5">
                  <MemberPicker
                    members={members}
                    value={val}
                    onChange={(v) => setVal(f.key, v)}
                    allowFreeText={!STRICT_MEMBER_KEYS.has(f.key)}
                    onPickMember={(m) => {
                      const folioField = FOLIO_PREFILL_MAP[f.key];
                      if (!folioField) return;
                      // When picking an existing member, fill folio + share_class
                      // + shares_held context. When picking "Other", clear them.
                      if (m) {
                        const patch = {
                          [f.key]: m.name || "",
                          [folioField]: m.folio_no || "",
                        };
                        if (m.member_type) patch.share_class = m.member_type;
                        setMany(patch);
                      } else {
                        setVal(folioField, "");
                      }
                    }}
                    testId={`${fieldPrefix}-${f.key}`}
                  />
                  {STRICT_MEMBER_KEYS.has(f.key) && (
                    <span className="text-[10px] text-amber-700 italic block mt-0.5">
                      Must be an existing member of this company.
                    </span>
                  )}
                </div>
              ) : f.type === "textarea" ? (
                <textarea
                  value={val}
                  onChange={(e) => setVal(f.key, e.target.value)}
                  placeholder={f.placeholder || ""}
                  rows={2}
                  className={`${common} mt-0.5`}
                  data-testid={`${fieldPrefix}-${f.key}`}
                />
              ) : (
                <input
                  type={
                    f.type === "number"
                      ? "number"
                      : f.type === "date"
                        ? "date"
                        : "text"
                  }
                  value={val}
                  onChange={(e) => setVal(f.key, e.target.value)}
                  placeholder={f.placeholder || ""}
                  className={`${common} mt-0.5`}
                  data-testid={`${fieldPrefix}-${f.key}`}
                />
              )}
              {f.help && (
                <span className="text-[10px] text-zinc-500 italic">
                  {f.help}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Coerce `number`-typed values from string to Number before sending to the
 * backend Pydantic model. Called when constructing the payload.
 */
export function coerceSpecificFieldNumbers(fields, values) {
  if (!fields || !values) return values;
  const out = { ...values };
  for (const f of fields) {
    if (f.type === "number" && out[f.key] !== "" && out[f.key] != null) {
      const n = Number(out[f.key]);
      if (!Number.isNaN(n)) out[f.key] = n;
    }
  }
  return out;
}
